import React, { useEffect, useMemo, useState } from 'react';
import WeeklyHeader from './WeeklyHeader';
import WeeklyAnalysisCard from './WeeklyAnalysisCard';
import FoodHighlightCard from './FoodHighlightCard';
import EmptyReportCard from './EmptyReportCard';

import CardSwiper from './CardSwiper';
import mascot from '../../images/common/img_main_mascot_03.svg';
import style from './DietReport.module.css';

import dayjs from 'dayjs';

import axiosApi from '../../api/daonAxios';
import { IMAGE_URL } from '../../constants/url';

const MEAL_TYPE_TEXT = {
    BKFST: '아침',
    LUNCH: '점심',
    DINNR: '저녁',
    SNACK: '간식',
};

// 기록이 없을 때 세 칸 모두 같은 제목을 쓴다
const EMPTY_TITLE = '이번 주 식단 기록이 없어요.';

// 응답이 빨라도 스켈레톤은 최소 이만큼 보여준다. 깜빡임 방지
const MIN_SKELETON_MS = 400;

const DietReport = () => {

    const weeks = useMemo(() => {
        const today = dayjs();

        // 리포트 주는 월~일 기준이다. dayjs 의 startOf('week') 는 일요일 시작이라 쓰지 않는다.
        // today.day() 는 일=0 ~ 토=6. +6 한 뒤 7로 나눈 나머지가 이번 주 월요일부터 지난 날 수다.
        const thisMonday = today.subtract((today.day() + 6) % 7, 'day').startOf('day');

        // 배치가 월요일 새벽에 '막 끝난 주'를 만든다. 이번 주는 아직 리포트가 없으므로 지난주가 마지막 칸이다.
        const lastMonday = thisMonday.subtract(1, 'week');

        return Array.from({ length: 12 }, (_, index) => {
            const start = lastMonday.subtract(11 - index, 'week');
            const end = start.add(6, 'day');

            return {
                startDe: start.format('YYYYMMDD'),
                endDe: end.format('YYYYMMDD'),
                range: `${start.format('YY.MM.DD')} ~ ${end.format('YY.MM.DD')}`,
            };
        });
    }, []);

    const [weekIdx, setWeekIdx] = useState(weeks.length - 1);   // 마지막 칸 = 막 끝난 주

    const week = weeks[weekIdx];

    const [goodMeals, setGoodMeals] = useState([]);
    const [badMeals, setBadMeals] = useState([]);
    const [totalMealCnt, setTotalMealCnt] = useState(0);   // 그 주 기록 전체 건수
    const [weekNo, setWeekNo] = useState(null);   // 가입한 주가 1주차. 아직 모르면 null
    const [nutrition, setNutrition] = useState(null);   // 주간 영양 분석. 그 주 리포트가 없으면 null
    const [aiAsstMsg, setAiAsstMsg] = useState(null);   // AI 문구. 아직 분석 전이면 null

    const [loading, setLoading] = useState(true);       // 주 전환 중이면 빈 상태 대신 스켈레톤

    useEffect(() => {

        if (!week) {
            return;
        }

        let ignore = false;      // 주를 빨리 넘겼을 때 이전 주 응답을 버린다
        let timer = null;

        setLoading(true);
        const startedAt = Date.now();

        // 최소 노출 시간을 채운 뒤에 끈다
        const stopLoading = () => {
            const rest = MIN_SKELETON_MS - (Date.now() - startedAt);

            if (rest <= 0) {
                setLoading(false);
                return;
            }

            timer = setTimeout(() => {
                if (!ignore) setLoading(false);
            }, rest);
        };

        const fetchReportData = async () => {
            try {
                const jToken = localStorage.getItem('jToken');

                const response = await axiosApi.get('/report/main', {
                    params: {
                        startDe: week.startDe,
                        endDe: week.endDe,
                    },
                    headers: {
                        Authorization: `Bearer ${jToken}`,
                    },
                });

                const result = response.data?.result || {};

                const goodMealsWithImage = await attachImages(result.goodMeals || [], jToken);
                const badMealsWithImage = await attachImages(result.badMeals || [], jToken);

                if (ignore) return;

                setGoodMeals(goodMealsWithImage);
                setBadMeals(badMealsWithImage);
                setTotalMealCnt(result.totalMealCnt ?? 0);
                setWeekNo(result.weekNo ?? null);
                setNutrition(result.nutrition || null);
                setAiAsstMsg(result.aiAsstMsg || null);

            } catch (error) {
                console.error(error);

                if (ignore) return;

                setGoodMeals([]);
                setBadMeals([]);
                setTotalMealCnt(0);
                setWeekNo(null);
                setNutrition(null);
                setAiAsstMsg(null);
            } finally {
                if (!ignore) stopLoading();
            }
        };

        fetchReportData();

        return () => {
            ignore = true;
            if (timer) clearTimeout(timer);
        };

    }, [week]);

    const attachImages = async (meals, jToken) => {
        return Promise.all(
            meals.map(async (item) => {

                const file = item.fileList?.[0];

                if (!file) {
                    return {
                        ...item,
                        image: null,
                    };
                }

                try {
                    const imageRes = await axiosApi.get(
                        IMAGE_URL + `${file.atchFileStr}&fileSn=${file.fileSn}`,
                        {
                            responseType: 'blob',
                            headers: {
                                Authorization: `Bearer ${jToken}`,
                            },
                        }
                    );

                    return {
                        ...item,
                        image: window.URL.createObjectURL(imageRes.data),
                    };

                } catch (error) {
                    console.error('이미지 조회 실패', error);

                    return {
                        ...item,
                        image: null,
                    };
                }
            })
        );
    };

    const formatMealDate = (recDe, ntktmTyCd) => {
        if (!recDe) return '';

        // 20260915 -> 2026-09-15
        const date = dayjs(
            `${recDe.substring(0, 4)}-${recDe.substring(4, 6)}-${recDe.substring(6, 8)}`
        );

        const dayText = ['일', '월', '화', '수', '목', '금', '토'];

        // 끼니 글자는 아는 값일 때만 붙인다. 모르는 값이면 날짜만 보여준다
        const mealText = MEAL_TYPE_TEXT[ntktmTyCd] || '';

        return `${date.format('M월 D일')} (${dayText[date.day()]})${mealText ? ' ' + mealText : ''}`;
    };

    const formatNumber = (value) => {
        if (value == null) return 0;

        return Number(value).toLocaleString('ko-KR', {
            maximumFractionDigits: 2,
        });
    };

    // 한 끼니가 카드 한 장이다. 제목은 그 끼니에 먹은 음식 목록, 영양은 끼니 합계다.
    const goodMealCards = useMemo(() => {
        return goodMeals.map((item, index) => ({
            dietSttusId: item.dietSttusId,
            rank: index + 1,
            image: item.image,
            title: item.dietFdCn,
            // info: `단백질 ${formatNumber(item.totPrtnVal)}g 나트륨 ${formatNumber(item.totSodmVal)}mg`,
            info: `단백질 ${(Math.round(Number(item.totPrtnVal ?? 0) * 10) / 10).toLocaleString('ko-KR')}g 나트륨 ${Math.round(Number(item.totSodmVal ?? 0)).toLocaleString('ko-KR')}mg`,
            date: formatMealDate(item.recDe, item.ntktmTyCd),
        }));
    }, [goodMeals]);

    const badMealCards = useMemo(() => {
        return badMeals.map((item, index) => ({
            dietSttusId: item.dietSttusId,
            rank: index + 1,
            image: item.image,
            title: item.dietFdCn,
            // info: `단백질 ${formatNumber(item.totPrtnVal)}g 나트륨 ${formatNumber(item.totSodmVal)}mg`,
            info: `단백질 ${(Math.round(Number(item.totPrtnVal ?? 0) * 10) / 10).toLocaleString('ko-KR')}g 나트륨 ${Math.round(Number(item.totSodmVal ?? 0)).toLocaleString('ko-KR')}mg`,
            date: formatMealDate(item.recDe, item.ntktmTyCd),
            warningText: item.dietClNm,   // 나트륨 과다 — 코드명을 그대로 쓴다
        }));
    }, [badMeals]);

    useEffect(() => {
        return () => {
            [...goodMeals, ...badMeals].forEach(item => {
                if (item.image) {
                    window.URL.revokeObjectURL(item.image);
                }
            });
        };
    }, [goodMeals, badMeals]);

    // 영양 분석은 기록이 있는 날이 하루라도 있어야 보여준다
    const hasNutrition = (nutrition?.recDayCo || 0) > 0;

    // 차트에 넘기는 배열은 참조가 고정되어야 한다. 매번 새로 만들면 차트가 계속 다시 그려진다
    const sodiumDaily  = useMemo(() => nutrition?.sodium?.daily  || [], [nutrition]);
    const calorieDaily = useMemo(() => nutrition?.calorie?.daily || [], [nutrition]);

    /*
     * AI 문구는 한 덩이로 내려온다. 첫 줄을 제목, 나머지를 본문으로 나눈다.
     * 기록이 하나도 없는 주는 AI 가 쓸 내용이 없어 문구가 오지 않는다.
     * 그때는 화면이 직접 안내 문구를 넣는다. 카드를 숨기면 주차와 날짜만 남아
     * 무엇을 해야 하는지 알 수 없다.
     */
    const aiMessage = useMemo(() => {
        if (aiAsstMsg) {
            const [firstLine, ...restLines] = aiAsstMsg.split('\n');

            return { title: firstLine, desc: restLines.join('\n') };
        }

        if (totalMealCnt === 0) {
            return {
                title: '다시 웰비와 시작해 볼까요? ✨',
                desc : '• 지난주 식단 기록이 없어 영양 리포트를 생성하지 못했어요. 📊\n'
                     + '• 나의 식단을 더 정밀하게 분석하기 위해 이번 주에는 가벼운 기록부터 시작해 볼까요? 🌱',
            };
        }

        return null;
    }, [aiAsstMsg, totalMealCnt]);

    // 실제 카드와 높이를 맞춘 스켈레톤. type: 'analysis' | 'food'
    const skeleton = (type) => (
        <div className={style.skRow} aria-hidden="true">
            {[0, 1].map(i => (
                <div key={i} className={style.skCard} data-type={type}>
                    {type === 'analysis' ? (
                        <>
                            <div className={style.skChart}>
                                {[30, 42, 88, 55, 72, 18, 24].map((h, j) => (
                                    <span key={j} style={{ height: `${h}%` }} />
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <span className={style.skThumb} />
                            <div className={style.skFoot}>
                                <div className={style.skLines}>
                                    <span className={style.skBar} style={{ width: '62%', height: 15 }} />
                                    <span className={style.skBar} style={{ width: '48%', height: 12, marginTop: 7 }} />
                                    <span className={style.skBar} style={{ width: '38%', height: 12, marginTop: 6 }} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ))}
        </div>
    );


    
    return (
        <div className={style.wrap}>
            <WeeklyHeader
                weekNum={weekNo}
                dateRange={week.range}
                message={aiMessage}
                mascotImg={mascot}
                loading={loading}
                onPrev={() => setWeekIdx(i => i - 1)}
                onNext={() => setWeekIdx(i => i + 1)}
                hasPrev={weekIdx > 0 && (weekNo == null || weekNo > 1)}
                hasNext={weekIdx < weeks.length - 1}
            />

            {/* 주간 영양 분석 */}
            <h3 className={style.sectionTitle}>주간 영양 분석</h3>
            {loading ? skeleton('analysis') : hasNutrition ? (
                <CardSwiper slidesPerView={1.08}>
                    <WeeklyAnalysisCard
                        title="평균 나트륨 섭취량"
                        // value={formatNumber(nutrition.sodium.avg)}
                         value={Math.round(Number(nutrition.sodium.avg ?? 0)).toLocaleString('ko-KR')}
                        unit="mg"
                        dailyValues={sodiumDaily}
                        threshold={nutrition.sodium.goal}
                        // highlightOver
                    />
                    <WeeklyAnalysisCard
                        title="평균 칼로리 섭취량"
                        // value={formatNumber(nutrition.calorie.avg)}
                        value={Math.round(Number(nutrition.calorie.avg ?? 0)).toLocaleString('ko-KR')}
                        unit="kcal"
                        dailyValues={calorieDaily}
                        threshold={nutrition.calorie.goal}
                        // highlightOver
                    />
                </CardSwiper>
            ) : (
                <EmptyReportCard
                    title={EMPTY_TITLE}
                    desc="식단을 기록하고 영양 분석을 확인하세요!"
                />
            )}

            {/* 이번주 우수한 식단 */}
            <h3 className={style.sectionTitle}>이번주 <em>우수한</em> 식단</h3>
            {/* <p className={style.sectionSub}>
                우수한 식단 기준 설명
            </p> */}
            {loading ? skeleton('food') : goodMealCards.length > 0 ? (
                <CardSwiper slidesPerView={1.1} spaceBetween={12}>
                    {goodMealCards.map((m, i) => (
                        <FoodHighlightCard key={m.dietSttusId ?? i} {...m} />
                    ))}
                </CardSwiper>
            ) : totalMealCnt === 0 ? (
                <EmptyReportCard
                    title={EMPTY_TITLE}
                    desc="식단을 입력하고 리포트를 확인해 보세요!"
                />
            ) : (
                <EmptyReportCard
                    title="이번 주 우수한 식단이 없어요."
                    desc="건강한 식단을 등록해 보세요!"
                />
            )}

            {/* 이번주 주의가 필요한 식단 */}
            <h3 className={style.sectionTitle}>이번주 <em className={style.warn}>주의</em>가 필요한 식단</h3>
            {/* <p className={style.sectionSub}>
                주의가 필요한 식단 기준 설명
            </p> */}
            {loading ? skeleton('food') : badMealCards.length > 0 ? (
                <CardSwiper slidesPerView={1.1} spaceBetween={12}>
                    {badMealCards.map((m, i) => (
                        <FoodHighlightCard key={m.dietSttusId ?? i} {...m} isWarning />
                    ))}
                </CardSwiper>
            ) : totalMealCnt === 0 ? (
                <EmptyReportCard
                    title={EMPTY_TITLE}
                    desc="식단을 입력하고 리포트를 확인해 보세요!"
                />
            ) : (
                <EmptyReportCard
                    title="이번 주 주의가 필요한 식단이 없어요."
                    desc="아주 훌륭해요!"
                />
            )}
        </div>
    );
};

export default DietReport;
